// ── MeetingMap 전체 상태 · 참조 · 파생 상태 관리 훅 ─────────────────────────
// - 모든 useState / useRef 선언
// - 시트 드래그 디스미스 이펙트 (non-passive touchmove)
// - body 스크롤 잠금, ESC 닫기 이펙트
// - useMemo 파생 상태 (validMeetings, selected, sortedFeedbackOptions)

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_ZOOM, MOBILE_BREAKPOINT, FIXED_FEEDBACK_KEYS,
  PULL_REFRESH_TRIGGER_PX,
  getKstTodayYmd, toTabLabel, toWeekdayLabel,
  parseCoord, isMapDisplayCoord,
} from '../../../components/meetingmap/utils';

export function useMeetingMapCore() {
  const baseDateRef = useRef(getKstTodayYmd());

  // ── 뷰 / 날짜 / 미팅 목록 ──────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [view, setView] = useState('map');
  const [visitDate, setVisitDate] = useState(getKstTodayYmd());
  const [loading, setLoading] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // ── 결과 입력 모달 ──────────────────────────────────────────────────────────
  const [resultOpen, setResultOpen] = useState(false);
  const [savingResult, setSavingResult] = useState(false);
  const [confirmContractOpen, setConfirmContractOpen] = useState(false);
  // 모달 열릴 때 DB 저장값 백업 — 마케터 킵 해제 불가 판단에 사용
  const [originalIsKeep, setOriginalIsKeep] = useState(false);
  const [resultForm, setResultForm] = useState({
    id: null,
    feedback: '',
    is_contracted: false,
    is_keep: false,
    contract_date: '',
    ticket_type: '',
    ticket_count: '',
    selectedProducts: [],
    productCounts: {},
    movieTicketSubType: '',
    productValidities: {},
    new_sales_person_name: '',
    new_visit_date: '',
    feedback_memo: '',
  });

  // ── 피드백 미입력 ───────────────────────────────────────────────────────────
  const [pendingList, setPendingList] = useState([]);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingLoading, setPendingLoading] = useState(false);

  // ── 참조 데이터 ─────────────────────────────────────────────────────────────
  const [salesPersons, setSalesPersons] = useState([]);
  const [feedbackOptions, setFeedbackOptions] = useState([]);
  const [contractTicketTypes, setContractTicketTypes] = useState([]);
  // 비내부 카테고리 목록 (카테고리명→기본상품 치환용 + 마커 색상 판정용)
  const [externalCategories, setExternalCategories] = useState([]);

  // ── 당겨서 새로고침 ─────────────────────────────────────────────────────────
  const [pullDistance, setPullDistance] = useState(0);
  const [pullRefreshing, setPullRefreshing] = useState(false);

  // ── 기타 UI 상태 ────────────────────────────────────────────────────────────
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const [keepLoading, setKeepLoading] = useState(false);
  const [keepListOpen, setKeepListOpen] = useState(false);
  const [keepList, setKeepList] = useState([]);
  const [keepListLoading, setKeepListLoading] = useState(false);

  // ── 시트 드래그 디스미스 ────────────────────────────────────────────────────
  const [sheetDragY, setSheetDragY] = useState(0);
  const sheetDragStartY  = useRef(0);
  const sheetDragging    = useRef(false);
  const sheetDragYRef    = useRef(0);
  const sheetHandleRef   = useRef(null);
  const SHEET_DISMISS_THRESHOLD = 110;

  // ── Leaflet / 지도 관련 Ref ─────────────────────────────────────────────────
  const mapContainerRef  = useRef(null);
  const mapRef           = useRef(null);
  const layerRef         = useRef(null);
  const markersRef       = useRef(new Map());
  const shouldFitRef     = useRef(true);
  const draggedPositions = useRef(new Map()); // Map<itemId, { lat, lng }>
  const isDraggingMarkerRef = useRef(false);  // 마커 드래그 중 — 당겨서새로고침 차단용
  const saveMarkerLayoutRef = useRef(null);   // 배치 저장 콜백 (handlers 가 주입, dragend 에서 호출)
  const leaderLayersRef  = useRef([]);        // SVG 렌더러 레이어 추적
  const prevSheetOpenRef = useRef(false);
  const sheetRef         = useRef(null);

  // ── 기타 Ref ────────────────────────────────────────────────────────────────
  const listWrapRef      = useRef(null);
  const pullStartYRef    = useRef(0);
  const pullActiveRef    = useRef(false);
  const pullDistanceRef  = useRef(0);

  // ── 핸들에만 non-passive 리스너 → 시트 내부 스크롤 영향 없음 ─────────────
  useEffect(() => {
    const el = sheetHandleRef.current;
    if (!el) return;
    const onStart = (e) => {
      sheetDragging.current   = true;
      sheetDragStartY.current = e.touches[0].clientY;
      sheetDragYRef.current   = 0;
      setSheetDragY(0);
    };
    const onMove = (e) => {
      if (!sheetDragging.current) return;
      e.preventDefault();
      e.stopPropagation(); // pull-to-refresh 방지
      const delta = e.touches[0].clientY - sheetDragStartY.current;
      if (delta > 0) { sheetDragYRef.current = delta; setSheetDragY(delta); }
    };
    const onEnd = () => {
      sheetDragging.current = false;
      if (sheetDragYRef.current >= SHEET_DISMISS_THRESHOLD) {
        setSheetDragY(0); setSheetOpen(false);
      } else {
        setSheetDragY(0);
      }
      sheetDragYRef.current = 0;
    };
    el.addEventListener('touchstart',  onStart, { passive: true });
    el.addEventListener('touchmove',   onMove,  { passive: false });
    el.addEventListener('touchend',    onEnd,   { passive: true });
    el.addEventListener('touchcancel', onEnd,   { passive: true });
    return () => {
      el.removeEventListener('touchstart',  onStart);
      el.removeEventListener('touchmove',   onMove);
      el.removeEventListener('touchend',    onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  // sheetHandleRef는 sheetOpen 바뀔 때마다 DOM이 재마운트되므로 의존성 포함
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetOpen]);

  // PC: Escape 키로 슬라이드 닫기
  useEffect(() => {
    if (!sheetOpen) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') setSheetOpen(false); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [sheetOpen]);

  // 시트 열릴 때 body + html 스크롤 잠금 (배경 따라움직임 방지) — **휴대폰만** (2026-09-28)
  //   휴대폰은 시트를 손가락으로 끌어올릴 때 뒤 페이지가 딸려 움직이지 않게 잠근다.
  //   PC 는 시트가 지도 상자 안의 정보 창일 뿐인데 잠금이 걸려, 창이 열려 있는 동안 전체 페이지가
  //   위아래로 스크롤되지 않았다(사용자 신고 — PC 는 body 가 스크롤 주인이라 body 잠금 = 페이지 정지).
  useEffect(() => {
    if (!sheetOpen || !isMobile) return;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [sheetOpen, isMobile]);

  // ── useMemo 파생 상태 ────────────────────────────────────────────────────────
  const validMeetings = useMemo(
    () => meetings
      .map((item) => ({
        ...item,
        _lat: parseCoord(item.latitude),
        _lng: parseCoord(item.longitude),
      }))
      .filter((item) =>
        item._lat !== null && item._lng !== null && isMapDisplayCoord(item._lat, item._lng)
      ),
    [meetings]
  );

  const selected = useMemo(
    () => meetings.find((item) => Number(item.id) === Number(selectedId)) || null,
    [meetings, selectedId]
  );

  // 고정 항목(미뤄짐/미팅인계)은 별도 UI로 분리되므로 그리드에서 제외
  const sortedFeedbackOptions = useMemo(() => {
    return feedbackOptions
      .map((o) => (typeof o === 'string' ? { name: o, auto_keep: false } : o))
      .filter((o) => !FIXED_FEEDBACK_KEYS.includes(o.name));
  }, [feedbackOptions]);

  // ── 계산된 파생값 (렌더마다 재계산, 메모 불필요) ───────────────────────────
  const todayYmd           = baseDateRef.current;
  const isTodaySelected    = visitDate === todayYmd;
  const centerDateLabel    = toTabLabel(visitDate);
  const centerWeekdayLabel = toWeekdayLabel(visitDate);
  const isPullReady        = pullDistance >= PULL_REFRESH_TRIGGER_PX;

  return {
    // 상태 + 세터
    isMobile, setIsMobile,
    view, setView,
    visitDate, setVisitDate,
    loading, setLoading,
    meetings, setMeetings,
    selectedId, setSelectedId,
    sheetOpen, setSheetOpen,
    resultOpen, setResultOpen,
    savingResult, setSavingResult,
    confirmContractOpen, setConfirmContractOpen,
    originalIsKeep, setOriginalIsKeep,
    resultForm, setResultForm,
    pendingList, setPendingList,
    pendingOpen, setPendingOpen,
    pendingLoading, setPendingLoading,
    salesPersons, setSalesPersons,
    feedbackOptions, setFeedbackOptions,
    contractTicketTypes, setContractTicketTypes,
    externalCategories, setExternalCategories,
    pullDistance, setPullDistance,
    pullRefreshing, setPullRefreshing,
    mapZoom, setMapZoom,
    keepLoading, setKeepLoading,
    keepListOpen, setKeepListOpen,
    keepList, setKeepList,
    keepListLoading, setKeepListLoading,
    sheetDragY, setSheetDragY,
    SHEET_DISMISS_THRESHOLD,
    // Refs
    baseDateRef,
    mapContainerRef, mapRef, layerRef, markersRef,
    shouldFitRef, draggedPositions, isDraggingMarkerRef, saveMarkerLayoutRef,
    leaderLayersRef, prevSheetOpenRef,
    sheetRef, listWrapRef,
    pullStartYRef, pullActiveRef, pullDistanceRef,
    sheetDragging, sheetHandleRef,
    // 파생 상태
    validMeetings, selected, sortedFeedbackOptions,
    // 계산된 표시값
    isTodaySelected, centerDateLabel, centerWeekdayLabel, isPullReady,
  };
}
